import { parseBsp, type Bsp } from '../shared/bsp';
import { CollisionModel } from '../shared/cm/cm';
import { MapLogic } from '../shared/game/mapLogic';
import { PlayerSession } from '../shared/game/session';
import { PhysicsWorld } from '../shared/world';
import type { VirtualFS } from '../fs/vfs';
import { MapScene, type PreparedScene } from '../render/mapRenderer';
import type { MapWorkerMessage, MapWorkerRequest } from './mapWorker';
import type { ShaderDef } from '../render/shaderScripts';
import type { TextureManager } from '../render/textures';

export interface LoadedMap {
  name: string;
  bsp: Bsp;
  world: PhysicsWorld;
  logic: MapLogic;
  session: PlayerSession;
  scene: MapScene;
}

/** Lists playable maps (maps/*.bsp) across all mounted pk3s. */
export function listMaps(vfs: VirtualFS): string[] {
  return vfs.list('maps', '.bsp').map((p) => p.slice('maps/'.length, -'.bsp'.length));
}

export { loadShaders } from '../render/shaderLoader';

export async function loadMap(
  name: string,
  vfs: VirtualFS,
  textures: TextureManager,
  shaders: Map<string, ShaderDef>,
  progress: (text: string) => void,
  maxTextureSize = 4096,
  simpleSky = false,
): Promise<LoadedMap> {
  // the BSP, collision, lightmaps and geometry in a worker when there is one (the page stays responsive)
  const archive = vfs.archiveOf(`maps/${name}.bsp`);
  const fromWorker =
    archive && typeof Worker !== 'undefined'
      ? await loadInWorker({ file: archive.file, archive: archive.name, map: name, maxTextureSize }, progress).catch((err) => {
          console.warn('map worker:', err);
          return null;
        })
      : null;
  let bsp: Bsp;
  let cm: CollisionModel;
  let prepared: PreparedScene | undefined;
  if (fromWorker) {
    ({ bsp, prepared } = fromWorker);
    // a structured clone: the methods come back with the prototype
    cm = Object.setPrototypeOf(fromWorker.cm, CollisionModel.prototype) as CollisionModel;
  } else {
    progress(`Loading maps/${name}.bsp`);
    const data = await vfs.read(`maps/${name}.bsp`);
    if (!data) throw new Error(`maps/${name}.bsp not found`);
    bsp = parseBsp(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer);
    progress('Building collision');
    cm = CollisionModel.fromBsp(bsp);
  }
  const world = new PhysicsWorld(cm);
  // the map script names the spawn objective (setautospawn)
  const logic = new MapLogic(bsp.entityString, world, (await vfs.readText(`maps/${name}.script`)) ?? undefined);
  const session = new PlayerSession(logic, world);

  progress('Building geometry');
  const scene = await MapScene.build({ bsp, mapName: name, entities: logic.entities, vfs, textures, shaders, prepared, maxTextureSize, simpleSky });
  return { name, bsp, world, logic, session, scene };
}

/** Runs mapWorker.ts once for a map. */
function loadInWorker(req: MapWorkerRequest, progress: (text: string) => void): Promise<Extract<MapWorkerMessage, { type: 'done' }>> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./mapWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (ev: MessageEvent<MapWorkerMessage>) => {
      const m = ev.data;
      if (m.type === 'progress') return progress(m.stage);
      worker.terminate();
      if (m.type === 'done') resolve(m);
      else reject(new Error(m.message));
    };
    worker.onerror = (ev) => {
      worker.terminate();
      reject(new Error(ev.message || 'the map worker failed'));
    };
    worker.postMessage(req);
  });
}
